import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import {
    VStack,
    Input,
    Textarea,
    useToast,
    SimpleGrid,
    HStack,
    Button,
    Divider,
    Text,
} from "@chakra-ui/react";
import {
    Training as Entity,
    TrainingApi as EntityApi,
    TrainingYear,
} from "../../../apis";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import { AppColorPicker, AppFormControl, AppIcon } from "../../../components";
import { APPC } from "../../../config";

export const TrainingForm: FC<{
    formContainerState: FormContainerStateReturnType;
}> = ({ formContainerState }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();

    // state & const
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Entity>(new Entity());

    const {
        handleSubmit,
        register,
        reset,
        control,
        setValue,
        formState: { errors },
    } = useForm<Entity>({
        resolver: yupResolver(schema(t)),
        mode: "onSubmit",
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "trainingYears",
    });

    // handler
    const onSubmitHandler = (formData: Entity) => {
        setLoading(true);
        EntityApi.createOrReplace<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.entityId) {
            reset();
            setData(new Entity());
            return;
        }
        setLoading(true);
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    setData(response);
                    const trainingYears: TrainingYear[] =
                        response?.trainingYears as TrainingYear[];
                    if (trainingYears) {
                        trainingYears.forEach((trainingYear) =>
                            append(trainingYear, { shouldFocus: false })
                        );
                    }
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    return (
        <AppFormContainer
            title={t("padm.TrainingForm:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
            size="xl"
        >
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl
                        label={t("ent.Training:name.label")}
                        isInvalid={!!errors.name}
                        message={errors.name?.message}
                        isLoading={loading}
                    >
                        <Input
                            type="text"
                            placeholder={t("ent.Training:name.placeholder")}
                            {...register("name")}
                            defaultValue={data.name}
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t("ent.Training:comment.label")}
                        isLoading={loading}
                    >
                        <Textarea
                            h="90px"
                            resize={"none"}
                            placeholder={t("ent.Training:comment.placeholder")}
                            {...register("comment")}
                            defaultValue={data.comment || ""}
                        />
                    </AppFormControl>
                    <Divider
                        borderColor={"var(--chakra-colors-greyT65)"}
                        mt={4}
                    />
                    <VStack gap={3} alignItems={"stretch"}>
                        <HStack justifyContent={"space-between"}>
                            <Text textStyle={"semiBoldLg"}>
                                {t("padm.TrainingForm:text.trainingYears")}
                            </Text>
                            <Button
                                variant={"unstyled"}
                                onClick={() =>
                                    append({
                                        code: "",
                                        color: "",
                                        year: "",
                                    } as TrainingYear)
                                }
                            >
                                <AppIcon name="ics-plus-circle" w="1.30rem" />
                            </Button>
                        </HStack>
                    </VStack>
                    <SimpleGrid columns={1} spacing={5} p={2}>
                        {fields.map((trainingYear, index) => (
                            <HStack
                                key={trainingYear.id}
                                justifyContent={"space-between"}
                                alignItems={"flex-start"}
                            >
                                <AppFormControl
                                    isLoading={loading}
                                    label={t("ent.TrainingYear:code.label")}
                                    isInvalid={
                                        !!errors?.trainingYears?.[index]?.[
                                            "code"
                                        ]
                                    }
                                    message={
                                        errors?.trainingYears?.[index]?.["code"]
                                            ?.message
                                    }
                                >
                                    <Input
                                        type="text"
                                        placeholder={t(
                                            "ent.TrainingYear:code.placeholder"
                                        )}
                                        {...register(
                                            `trainingYears.${index}.code`
                                        )}
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    isLoading={loading}
                                    label={t("ent.TrainingYear:color.label")}
                                    isInvalid={
                                        !!errors?.trainingYears?.[index]?.[
                                            "color"
                                        ]
                                    }
                                    message={
                                        errors?.trainingYears?.[index]?.[
                                            "color"
                                        ]?.message
                                    }
                                >
                                    <AppColorPicker
                                        value={
                                            (trainingYear as TrainingYear)
                                                ?.color || undefined
                                        }
                                        placeholder={t(
                                            "ent.TrainingYear:color.placeholder"
                                        )}
                                        {...register(
                                            `trainingYears.${index}.color`
                                        )}
                                        onChange={(hex) =>
                                            setValue(
                                                `trainingYears.${index}.color`,
                                                hex
                                            )
                                        }
                                    />
                                </AppFormControl>
                                <AppFormControl
                                    isLoading={loading}
                                    label={t("ent.TrainingYear:year.label")}
                                    isInvalid={
                                        !!errors?.trainingYears?.[index]?.[
                                            "year"
                                        ]
                                    }
                                    message={
                                        errors?.trainingYears?.[index]?.["year"]
                                            ?.message
                                    }
                                >
                                    <Input
                                        type="text"
                                        placeholder={t(
                                            "ent.TrainingYear:year.placeholder"
                                        )}
                                        {...register(
                                            `trainingYears.${index}.year`
                                        )}
                                    />
                                </AppFormControl>
                                <Button
                                    variant={"unstyled"}
                                    onClick={() => remove(index)}
                                    color={"var(--chakra-colors-destructive)"}
                                    mt={"28px"}
                                >
                                    <AppIcon name="icl-trash" w="1.25rem" />
                                </Button>
                            </HStack>
                        ))}
                    </SimpleGrid>
                </VStack>
            </form>
        </AppFormContainer>
    );
};
